import React, { useState } from 'react';

import { Collapsible, CollapsibleContent, CollapsibleContextConsumer, CollapsibleHeading } from '$partials/base/collapsible';

import {
    IconChevronSprite,
    IconMinusSprite,
    IconPlusSprite
} from '$partials/icons/index.js';
import { Button } from '$partials/ui';
import { useLocale } from '$contexts/locale.jsx';
import RichText from '$partials/global/rich-text';
import { AnimatePresence, easeOut, motion } from 'framer-motion';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modules.d.ts').ModuleAccordion}
 */

const ModuleAccordion = ({
    bodyCopy,
    cta1Label,
    cta1Link,
    cta2Label,
    cta2Link,
    eyebrow,
    heading,
    accordionItems,
    itemsCollapsible,
    isArticlePage,
}) => {
    const [activePanel, setActivePanel] = useState(0);

    const {
        formatURI,
        translate: t,
    } = useLocale();

    return (
        <section
            className={`relative py-40${isArticlePage ? '' : ' md:py-80'} bg-white rounded`}>
            <div className='relative z-10 layout-container layout-site-outer-padding w-full overflow-hidden'>
                <div className='col-wrapper flex md:flex-row flex-col gap-y-40'>
                    <div className='flex flex-col col-padding md:w-6/12'>
                        {eyebrow && (
                            <p className='type-style-eyebrow text-text-accent mb-12 md:mb-16'>
                                {eyebrow}
                            </p>
                        )}

                        {/* hero text */}
                        {heading && (
                            <div className={`w-full ${isArticlePage ? 'type-style-4' : 'type-style-3-extended'} break-words text-secondary`}>
                                {heading}
                            </div>
                        )}

                        {/* body copy */}
                        {bodyCopy && (
                            <p className='text-text-secondary type-style-body mt-10 md:mt-16 md:w-5/6'>
                                {bodyCopy}
                            </p>
                        )}

                        {/* cta buttons */}
                        {(cta1Link || cta2Link) && (
                            <div className='flex flex-wrap gap-8 mt-24 md:mt-32'>
                                {cta1Link && (
                                    <Button
                                        as='a'
                                        className='button button--primary'
                                        href={formatURI(cta1Link)}>
                                        <span className='button__label--animate'>
                                            <span>{cta1Label}</span>
                                        </span>
                                        <span className='button__icon button__icon--animate'>
                                            <IconChevronSprite />
                                        </span>
                                    </Button>
                                )}
                                {cta2Link && (
                                    <Button
                                        as='a'
                                        className='button button--secondary'
                                        href={formatURI(cta2Link)}>
                                        <span className='button__label--animate'>
                                            <span>{cta2Label}</span>
                                        </span>
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>

                    {(itemsCollapsible && accordionItems?.items?.length > 0) && (
                        <div className='w-full md:w-6/12 col-padding flex flex-col gap-y-20 md:gap-y-[18.5px]'>
                            {accordionItems?.items?.map((item, index) => {
                                return (
                                    <Collapsible
                                        getState={({isOpened}) => isOpened && setActivePanel(index)}
                                        id={`accordion-${index}`}
                                        isOpened={index === 0}
                                        key={`accordion-${index}`}
                                        open={activePanel === index}>
                                        <CollapsibleHeading
                                            className='w-full pt-20 md:pt-[18.5px] border-t border-border-default group'>
                                            <CollapsibleContextConsumer>
                                                {({ isOpened }) => (
                                                    <span className='flex items-center justify-between'>
                                                        {item?.heading && (
                                                            <p className='type-style-body text-left w-10/12 sm:w-6/8 md:w-full mr-60'>{item.heading}</p>
                                                        )}
                                                        <span
                                                            aria-hidden='true'
                                                            className='w-40 h-48 flex items-center justify-center rounded text-brand-500 group-hover:bg-brand-200'>
                                                            <span className='overflow-hidden inline-block w-16'>
                                                                {isOpened ?
                                                                    <IconMinusSprite className='group-hover:-translate-x-1/2 transition-transform ease-sprite duration-500 block' />
                                                                    :
                                                                    <IconPlusSprite className='group-hover:-translate-x-1/2 transition-transform ease-sprite duration-500 block' />
                                                                }
                                                            </span>
                                                        </span>
                                                    </span>
                                                )}
                                            </CollapsibleContextConsumer>
                                        </CollapsibleHeading>
                                        <CollapsibleContextConsumer>
                                            {({ isOpened, ids }) => (
                                                <>
                                                    <AnimatePresence initial={false}>
                                                        {(isOpened) && (
                                                            <motion.div
                                                                animate={{ opacity: 1, height: 'auto' }}
                                                                aria-labelledby={ids.heading}
                                                                exit={{ opacity: 0, height: 0 }}
                                                                initial={{ opacity: 0, height: 0 }}
                                                                style={{ overflow: 'hidden' }}
                                                                transition={{ duration: 0.2 }}>
                                                                {item?.body?.json && (
                                                                    <RichText
                                                                        className='text-text-secondary mt-[14.5px] rich-text accordion-module md:w-4/6'
                                                                        json={item.body.json} />
                                                                )}
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                </>
                                            )}
                                        </CollapsibleContextConsumer>
                                    </Collapsible>
                                );
                            })}
                        </div>
                    )}
                    {(!itemsCollapsible && accordionItems?.items?.length > 0) && (
                        <div className='w-full md:w-6/12 col-padding flex flex-col gap-y-20 md:gap-y-[18.5px]'>
                            {accordionItems?.items?.map((item, index) => {
                                return (
                                    <div
                                        className='pt-20 md:pt-[18.5px] border-t border-border-default'
                                        key={`accordion-item-${index}`}>
                                        {item?.heading && (
                                            <p className='type-style-body text-text-primary text-left w-10/12 sm:w-6/8 md:w-full mr-60'>{item.heading}</p>
                                        )}

                                        {item?.body?.json &&
                                            <RichText
                                                className='text-text-secondary mt-[14.5px] rich-text accordion-module md:mr-60'
                                                json={item.body.json} />
                                        }
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </section>

    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

export default ModuleAccordion;
